import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Input, Select, Textarea, Button, showToast } from '../components/UI';

const CATEGORIES = [
  { value: 'cellphones', label: 'Cellphones' },
  { value: 'tablets', label: 'Tablets' },
  { value: 'laptops', label: 'Laptops' },
  { value: 'servers', label: 'Servers' },
  { value: 'hard_drives', label: 'Hard Drives' },
  { value: 'ddr_ram', label: 'DDR RAM' },
  { value: 'other', label: 'Other' },
];

const GRADES = [
  { value: 'A', label: 'Grade A — Fully functional, no noticeable cosmetic issue' },
  { value: 'B', label: 'Grade B — Fully functional, slightly used' },
  { value: 'C', label: 'Grade C — Fully functional, heavily used' },
  { value: 'D', label: 'Grade D — Functional and cosmetic defective' },
  { value: 'E', label: 'Grade E — Scrap' },
  { value: 'X', label: 'Grade X — Untested' },
];

export default function CreateListingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', modelNumber: '', description: '', specifications: '',
    category: 'laptops', grade: 'A', quantity: '', quantityUnit: 'each',
    condition: '', startingPrice: '', durationHours: '72',
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const imageCount = files.filter(f => f.type.startsWith('image/')).length;
  const videoCount = files.filter(f => f.type.startsWith('video/')).length;
  const fileReqMet = imageCount >= 3 && videoCount >= 1;

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length + files.length > 15) {
      showToast('Maximum 15 files allowed', 'error');
      return;
    }
    setFiles(prev => [...prev, ...selected]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title || !form.modelNumber || !form.description || !form.condition || !form.startingPrice || !form.quantity) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    if (!fileReqMet) {
      showToast(`Listing requires at least 3 images and 1 video. You have ${imageCount} images and ${videoCount} videos.`, 'error');
      return;
    }

    setLoading(true);
    try {
      const listing = await api.createListing({
        ...form,
        startingPrice: Number(form.startingPrice),
        quantity: Number(form.quantity),
        durationHours: Number(form.durationHours),
      });

      if (files.length > 0) {
        try {
          await api.uploadListingFiles(listing.id, files);
        } catch (err) {
          showToast('Listing created but file upload failed: ' + err.message, 'error');
        }
      }

      showToast('Listing created successfully!');
      navigate('/my-listings');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 32, maxWidth: 720, margin: '0 auto' }}>
      <button onClick={() => navigate('/my-listings')} style={{
        background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
        fontFamily: 'var(--sans)', fontSize: 14, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 6,
      }}>← Back to My Listings</button>

      <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)', fontFamily: 'var(--sans)', letterSpacing: '-0.02em', marginBottom: 8 }}>
        Create New Listing
      </h1>
      <p style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 32 }}>
        All fields marked with * are required. Listings must include at least 3 photos and 1 video.
      </p>

      <form onSubmit={handleSubmit} style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 32,
      }}>
        {/* Product Info */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Product Information</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input label="Product Title" value={form.title} onChange={update('title')} placeholder="e.g. Dell PowerEdge R740 Server" required />
            <Input label="Model Name / Number" value={form.modelNumber} onChange={update('modelNumber')} placeholder="e.g. R740, iPhone 13 Pro, ThinkPad X1 Carbon Gen 9" required mono />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Select label="Category" value={form.category} onChange={update('category')} required>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
              <Select label="Grade" value={form.grade} onChange={update('grade')} required>
                {GRADES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </Select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
              <Input label="Quantity" value={form.quantity} onChange={update('quantity')} placeholder="e.g. 50" type="number" required />
              <Select label="Unit" value={form.quantityUnit} onChange={update('quantityUnit')} required>
                <option value="each">Pieces</option>
                <option value="lbs">Pounds (lbs)</option>
              </Select>
            </div>
            <Input label="Condition Details" value={form.condition} onChange={update('condition')} placeholder="e.g. All units tested, batteries hold 80%+ charge" required />
            <Textarea label="Description" value={form.description} onChange={update('description')} placeholder="Describe the product, its history, and what's included..." rows={4} />
            <Textarea label="Specifications" value={form.specifications} onChange={update('specifications')} placeholder="CPU, RAM, storage, screen size, etc." rows={3} />
          </div>
        </div>

        {/* Auction Settings */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Auction Settings</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Input label="Starting Price (USD)" value={form.startingPrice} onChange={update('startingPrice')} placeholder="100" type="number" prefix="$" mono required />
            <Select label="Auction Duration" value={form.durationHours} onChange={update('durationHours')}>
              <option value="24">24 hours</option>
              <option value="48">48 hours</option>
              <option value="72">72 hours (default)</option>
              <option value="120">5 days</option>
              <option value="168">7 days</option>
            </Select>
          </div>
        </div>

        {/* File Upload */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Images & Videos *</div>
            <div style={{
              fontSize: 12, fontFamily: 'var(--mono)', padding: '4px 12px', borderRadius: 100,
              background: fileReqMet ? 'var(--accent-dim)' : 'var(--amber-dim)',
              color: fileReqMet ? 'var(--accent)' : 'var(--amber)',
              border: `1px solid ${fileReqMet ? 'rgba(34,214,122,0.2)' : 'rgba(245,158,11,0.2)'}`,
            }}>
              {imageCount}/3 images · {videoCount}/1 video {fileReqMet ? '✓' : ''}
            </div>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {files.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12,
                }}>
                  <span style={{ fontSize: 14 }}>{f.type.startsWith('video/') ? '🎬' : '📷'}</span>
                  <span style={{ color: 'var(--text2)', fontFamily: 'var(--mono)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  <span style={{ color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                    ({(f.size / 1024 / 1024).toFixed(1)}MB)
                  </span>
                  <button onClick={() => removeFile(i)} style={{
                    background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, padding: '0 4px',
                  }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Upload area */}
          <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 32, cursor: 'pointer',
            border: `2px dashed ${fileReqMet ? 'rgba(34,214,122,0.3)' : 'var(--border2)'}`,
            borderRadius: 'var(--radius)', background: 'var(--accent-dim2)', transition: 'all 0.2s',
          }}>
            <span style={{ fontSize: 32 }}>📸</span>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>Click to upload images and videos</span>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
              Minimum: 3 images + 1 video · Max 15 files
            </span>
            <input type="file" multiple accept="image/*,video/*" onChange={handleFiles} style={{ display: 'none' }} />
          </label>

          {!fileReqMet && files.length > 0 && (
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: 'var(--radius)',
              background: 'var(--amber-dim)', border: '1px solid rgba(245,158,11,0.2)',
              fontSize: 12, color: 'var(--amber)', fontFamily: 'var(--mono)',
            }}>
              ⚠ {imageCount < 3 ? `Need ${3 - imageCount} more image${3 - imageCount > 1 ? 's' : ''}. ` : ''}
              {videoCount < 1 ? 'Need 1 video clip.' : ''}
            </div>
          )}
        </div>

        {/* Info box */}
        <div style={{
          background: 'var(--accent-dim2)', border: '1px solid rgba(34,214,122,0.15)', borderRadius: 'var(--radius)',
          padding: 16, marginBottom: 24, fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)', lineHeight: 1.7,
        }}>
          Your listing will go live immediately and the auction timer will start. Once offers are placed, the listing cannot be edited or cancelled. The highest bidder when the timer expires will be awarded the item.
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => navigate('/my-listings')}>Cancel</Button>
          <Button type="submit" disabled={loading || !fileReqMet}>
            {loading ? 'Creating...' : 'Publish Listing →'}
          </Button>
        </div>
      </form>
    </div>
  );
}
